/**
 * 会社の登録・編集ポップアップ(NEXUS Gate のマスタと同じ作り)。
 * - 会社単価の変更は「＋ 改訂を追加」でのみ行う(price_history に必ず残る)
 * - 登録日・最終更新・変更履歴(監査ログ)を表示する
 */
import { useEffect, useState, type FormEvent } from 'react';
import Modal from '@core/components/Modal';
import NumInput from '@core/components/NumInput';
import { useToast } from '@core/components/Toast';
import { useAuth } from '@core/auth/AuthProvider';
import { auditText, fetchAuditLogs, fmtTs, diffLines } from '@core/lib/auditLog';
import { todayStr, toDateLabel } from '@core/lib/date';
import { formatYen, parseAmount } from '@core/lib/money';
import {
  BUSINESS_LABELS,
  COMPANY_STATUS_LABELS,
  type AuditLog,
  type BusinessType,
  type Company,
  type CompanyStatus,
  type UserDoc,
  type DeliveryPlan,
  type DateStr,
  WEEKDAY_LABELS,
} from '@core/lib/types';
import { createCompany, deleteCompany, updateCompany, type CompanyInput, type PriceRevisionInput } from './companyRepo';
import { fetchUsers } from './userRepo';

type RevisionRow = { effective_from: string; priceText: string; reason: string };

const inputStyle = { width: '100%', padding: '6px 8px', border: '1px solid var(--line)', borderRadius: 6, fontFamily: 'inherit', fontSize: 13 } as const;

export default function CompanyEditModal({
  company,
  onClose,
  onSaved,
}: {
  company: Company | null; // null=新規登録
  onClose: () => void;
  onSaved: () => void;
}) {
  const toast = useToast();
  const { user } = useAuth();
  const isNew = company === null;

  const [name, setName] = useState(company?.name ?? '');
  const [price, setPrice] = useState(company ? String(company.unit_price) : '');
  const [productType, setProductType] = useState<BusinessType>(company?.product_type ?? 'kyutoki');
  const [deliveryTiming, setDeliveryTiming] = useState(company?.delivery_timing ?? '');
  const [bbCodes, setBbCodes] = useState((company?.bb_business_codes ?? []).join('; '));
  const [phone, setPhone] = useState(company?.phone ?? '');
  const [status, setStatus] = useState<CompanyStatus>(company?.status ?? 'active');
  const [note, setNote] = useState(company?.note ?? '');
  /** 曜日別の納品件数(2026-09-24 D-145)。適用開始月ごとに 月〜日 の7つ */
  const [plans, setPlans] = useState<DeliveryPlan[]>(company?.delivery_plan ?? []);
  const setPlan = (i: number, patch: Partial<DeliveryPlan>) => setPlans((prev) => prev.map((p, j) => (j === i ? { ...p, ...patch } : p)));
  const setPlanCount = (i: number, day: number, v: string) =>
    setPlans((prev) => prev.map((p, j) => (j === i ? { ...p, counts: p.counts.map((c, k) => (k === day ? Math.max(0, Number(v || 0)) : c)) } : p)));
  const [revisions, setRevisions] = useState<RevisionRow[]>([]);
  const [busy, setBusy] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);

  const [history, setHistory] = useState<AuditLog[] | null>(null);
  const [historyError, setHistoryError] = useState(false);
  const [userNames, setUserNames] = useState<Map<string, string>>(new Map());
  const nameOf = (id: string | null | undefined) => (id ? (userNames.get(id) ?? id) : '—');

  useEffect(() => {
    void fetchUsers()
      .then((list: UserDoc[]) => setUserNames(new Map(list.map((u) => [u.id, u.name]))))
      .catch(() => undefined); // 登録者名の表示用。失敗時はIDのまま出す
    if (company) {
      void fetchAuditLogs('company', company.id)
        .then(setHistory)
        .catch(() => {
          setHistory([]);
          setHistoryError(true);
        });
    }
  }, [company]);

  function parseCodes(): string[] {
    return bbCodes.split(/[;；,、]/).map((v) => v.trim()).filter((v) => v !== '');
  }

  function parseRevisions(): PriceRevisionInput[] {
    return revisions.map((r) => {
      const p = parseAmount(r.priceText);
      if (p === null) throw new Error('改訂後の単価は0以上の整数(円)で入力してください');
      return { effective_from: r.effective_from, price: p, reason: r.reason };
    });
  }

  function describeChanges(current: Company, input: CompanyInput, revs: PriceRevisionInput[]): string[] {
    const { pair, lines } = diffLines();
    pair('社名', current.name, input.name.trim());
    pair('商材', BUSINESS_LABELS[current.product_type], BUSINESS_LABELS[input.product_type]);
    pair('納品タイミング', current.delivery_timing, input.delivery_timing.trim());
    pair('BB業務コード', current.bb_business_codes.join('; '), input.bb_business_codes.join('; '));
    pair('電話番号', current.phone ?? '', input.phone.trim());
    pair('ステータス', COMPANY_STATUS_LABELS[current.status], COMPANY_STATUS_LABELS[input.status]);
    pair('備考', current.note ?? '', input.note.trim());
    for (const r of revs) lines.push(`改訂を追加: ${toDateLabel(r.effective_from)}から 会社単価 ${formatYen(r.price)}円(${r.reason})`);
    return lines;
  }

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (!user) return;
    setBusy(true);
    try {
      const revs = parseRevisions();
      if (isNew) {
        const priceNum = parseAmount(price || '0');
        if (priceNum === null) throw new Error('会社単価は0以上の整数(円)で入力してください');
        await createCompany(
          { name, unit_price: priceNum, product_type: productType, delivery_timing: deliveryTiming, bb_business_codes: parseCodes(), phone, status, note, delivery_plan: plans },
          user.id,
          { revisions: revs },
        );
        toast('会社を登録しました');
      } else {
        const input: CompanyInput = {
          name,
          unit_price: company.unit_price,
          product_type: productType,
          delivery_timing: deliveryTiming,
          bb_business_codes: parseCodes(),
          phone,
          status,
          note,
          delivery_plan: plans,
        };
        await updateCompany({ current: company, input, revisions: revs, changes: describeChanges(company, input, revs), userId: user.id });
        toast('保存しました');
      }
      onSaved();
    } catch (err) {
      toast(err instanceof Error ? err.message : '保存に失敗しました');
    } finally {
      setBusy(false);
    }
  }

  async function handleDelete() {
    if (!company || !user) return;
    setBusy(true);
    try {
      await deleteCompany(company, user.id);
      toast('削除しました');
      onSaved();
    } catch (err) {
      toast(err instanceof Error ? err.message : '削除に失敗しました');
    } finally {
      setBusy(false);
    }
  }

  const upcoming = company?.price_history?.filter((h) => h.effective_from > todayStr()) ?? [];

  return (
    <Modal onClose={onClose} width="min(700px, 94vw)">
      <h3>{isNew ? '会社の新規登録' : `${company.name}(${company.id})の編集`}</h3>
      {company && (
        <p className="hint" style={{ margin: '4px 0 0' }}>
          登録日: {fmtTs(company.created_at)} {nameOf(company.created_by)} ／ 最終更新: {fmtTs(company.updated_at)} {nameOf(company.updated_by)}
        </p>
      )}

      <form onSubmit={handleSubmit}>
        <div className="form-grid" style={{ marginTop: 14, gridTemplateColumns: 'repeat(2, minmax(0, 1fr))' }}>
          <div>
            <label>会社ID</label>
            <input readOnly disabled value={company?.id ?? 'システム自動付与'} style={{ background: 'var(--legacy-shade)', color: 'var(--muted)' }} />
          </div>
          <div>
            <label>社名 *</label>
            <input value={name} onChange={(e) => setName(e.target.value)} required autoFocus={isNew} />
          </div>
          {isNew ? (
            <div>
              <label>会社単価(納品1件あたり・円)*</label>
              <NumInput value={price} onChange={setPrice} required />
            </div>
          ) : (
            <div>
              <label>現在の会社単価(変更は下の「改訂を追加」から)</label>
              <input readOnly disabled value={`${formatYen(company.unit_price)}円`} style={{ background: 'var(--legacy-shade)', color: 'var(--ink)', fontWeight: 700 }} />
              {upcoming.length > 0 && (
                <div className="hint" style={{ marginTop: 4 }}>
                  次回改訂: {upcoming.map((h) => `${toDateLabel(h.effective_from)}から ${formatYen(h.price)}円`).join(' / ')}
                </div>
              )}
            </div>
          )}
          <div>
            <label>商材</label>
            <select value={productType} onChange={(e) => setProductType(e.target.value as BusinessType)}>
              {(Object.entries(BUSINESS_LABELS) as [BusinessType, string][]).map(([k, v]) => (
                <option key={k} value={k}>
                  {v}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label>納品タイミング(前日 / 土曜日 など)</label>
            <input value={deliveryTiming} onChange={(e) => setDeliveryTiming(e.target.value)} />
          </div>
          <div>
            <label>ステータス</label>
            <select value={status} onChange={(e) => setStatus(e.target.value as CompanyStatus)}>
              {(Object.entries(COMPANY_STATUS_LABELS) as [CompanyStatus, string][]).map(([k, v]) => (
                <option key={k} value={k}>
                  {v}
                </option>
              ))}
            </select>
          </div>
          <div className="fw">
            <label>BB業務コード(複数は ; 区切り)</label>
            <input value={bbCodes} onChange={(e) => setBbCodes(e.target.value)} placeholder="例) GYU-A; GYU-B" />
          </div>
          <div>
            <label>電話番号</label>
            <input value={phone} onChange={(e) => setPhone(e.target.value)} />
          </div>
          <div>
            <label>備考</label>
            <input value={note} onChange={(e) => setNote(e.target.value)} />
          </div>
        </div>

        {/* ---- 曜日別の納品件数(2026-09-24 発注者指示。締め作業の初期値) ---- */}
        <div className="gray-box">
          <div className="box-title">納品件数の予定(曜日別)</div>
          <p className="hint" style={{ margin: '0 0 8px' }}>
            締め作業の「納品件数」の<b>初期値</b>に使います。途中で変わる場合は行を足して<b>適用開始日</b>を入れてください(その日から次の予定まで続きます)。祝日は考えません
          </p>
          {plans.length > 0 && (
            <table style={{ fontSize: 12.5, marginBottom: 8 }}>
              <thead>
                <tr>
                  <th>適用開始日</th>
                  {WEEKDAY_LABELS.map((w) => (
                    <th key={w} className="num">
                      {w}
                    </th>
                  ))}
                  <th />
                </tr>
              </thead>
              <tbody>
                {plans.map((p, i) => (
                  <tr key={i}>
                    <td>
                      <input type="date" value={p.from} onChange={(e) => setPlan(i, { from: e.target.value as DateStr })} style={{ width: 150 }} />
                    </td>
                    {WEEKDAY_LABELS.map((w, d) => (
                      <td key={w} className="num">
                        <input inputMode="numeric" value={String(p.counts[d] ?? 0)} onChange={(e) => setPlanCount(i, d, e.target.value)} style={{ width: 52, textAlign: 'right' }} />
                      </td>
                    ))}
                    <td>
                      <button type="button" className="txn-link" onClick={() => setPlans((prev) => prev.filter((_, j) => j !== i))}>
                        削除
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          <button type="button" className="txn-link" onClick={() => setPlans((prev) => [...prev, { from: (prev[prev.length - 1]?.from ?? todayStr()) as DateStr, counts: [0, 0, 0, 0, 0, 0, 0] }])}>
            ＋ 予定を追加
          </button>
        </div>

        {/* ---- 会社単価の改訂 ---- */}
        <div className="gray-box">
          <div className="box-title">会社単価の改訂履歴</div>
          {company && (company.price_history?.length ?? 0) > 0 && (
            <table style={{ fontSize: 12.5, marginBottom: 8 }}>
              <thead>
                <tr>
                  <th>適用開始日</th>
                  <th className="num">単価</th>
                  <th>理由</th>
                  <th>登録者</th>
                  <th>登録日時</th>
                </tr>
              </thead>
              <tbody>
                {[...company.price_history].reverse().map((h, i) => (
                  <tr key={i}>
                    <td>{toDateLabel(h.effective_from)}</td>
                    <td className="num">{formatYen(h.price)}円</td>
                    <td>{h.reason}</td>
                    <td>{nameOf(h.changed_by)}</td>
                    <td>{fmtTs(h.changed_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          {revisions.map((r, i) => (
            <div key={i} style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 6, flexWrap: 'wrap' }}>
              <input
                type="date"
                value={r.effective_from}
                onChange={(e) => setRevisions((prev) => prev.map((x, j) => (j === i ? { ...x, effective_from: e.target.value } : x)))}
                style={{ ...inputStyle, flex: '0 1 160px' }}
              />
              <NumInput
                placeholder="改訂後の単価(円)"
                value={r.priceText}
                onChange={(v) => setRevisions((prev) => prev.map((x, j) => (j === i ? { ...x, priceText: v } : x)))}
                style={{ ...inputStyle, flex: '0 1 160px' }}
              />
              <input
                placeholder="理由"
                value={r.reason}
                onChange={(e) => setRevisions((prev) => prev.map((x, j) => (j === i ? { ...x, reason: e.target.value } : x)))}
                style={{ ...inputStyle, flex: '1 1 200px' }}
              />
              <button type="button" className="txn-link" onClick={() => setRevisions((prev) => prev.filter((_, j) => j !== i))}>
                取消
              </button>
            </div>
          ))}
          <button
            type="button"
            className="btn-line"
            style={{ padding: '6px 14px' }}
            onClick={() => setRevisions((prev) => [...prev, { effective_from: todayStr(), priceText: '', reason: '' }])}
          >
            ＋ 改訂を追加
          </button>
        </div>

        {/* ---- 変更履歴 ---- */}
        {company && (
          <div className="gray-box">
            <div className="box-title">変更履歴</div>
            {history === null ? (
              <div className="hint">読み込み中…</div>
            ) : historyError ? (
              <div className="hint">変更履歴を読み込めませんでした。</div>
            ) : history.length === 0 ? (
              <div className="hint">変更履歴はまだありません。</div>
            ) : (
              <table className="no-wrap-table" style={{ fontSize: 12.5 }}>
                <thead>
                  <tr>
                    <th>日時</th>
                    <th>変更者</th>
                    <th>内容</th>
                  </tr>
                </thead>
                <tbody>
                  {history.map((h) => (
                    <tr key={h.id}>
                      <td>{fmtTs(h.at)}</td>
                      <td>{nameOf(h.by)}</td>
                      <td style={{ whiteSpace: 'normal' }}>{auditText(h)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        )}

        <div className="modal-actions">
          <button className="btn-green" type="submit" disabled={busy}>
            {busy ? '保存中…' : isNew ? '登録する' : '保存する'}
          </button>
          <button className="btn-line" type="button" onClick={onClose}>
            キャンセル
          </button>
          {!isNew && (
            <button className="btn-line danger" type="button" disabled={busy} onClick={() => setConfirmDelete(true)}>
              削除
            </button>
          )}
        </div>
      </form>

      {confirmDelete && company && (
        <Modal onClose={() => setConfirmDelete(false)} width="min(420px, 94vw)" showClose={false}>
          <h3>削除しますか?</h3>
          <p className="hint" style={{ margin: '10px 0' }}>
            {company.name}({company.id})を完全に削除します。取り消せません。
            <br />
            取引が終了した場合は削除ではなく、ステータスを「停止」に変更してください。
          </p>
          <div className="modal-actions">
            <button className="btn-line" onClick={() => setConfirmDelete(false)}>
              キャンセル
            </button>
            <button className="btn-sm-ng" disabled={busy} onClick={() => void handleDelete()}>
              完全に削除する
            </button>
          </div>
        </Modal>
      )}
    </Modal>
  );
}
